import { useEffect, useRef, useState } from "react";
import { MoreHorizontal, Flag, Ban, Share2, X } from "lucide-react";
import { mirror } from "@/lib/syncStore";
import { SUPABASE_TABLES } from "@/lib/supabaseClient";
import { useAuth } from "@/context/AuthContext";
import { toggleBlocked, getBlocked } from "@/lib/monetizationStore";

const REASONS = [
    { id: "spam",          label: "Spam or scam" },
    { id: "harassment",    label: "Harassment or hate" },
    { id: "inappropriate", label: "Inappropriate content" },
    { id: "impersonation", label: "Impersonation / fake account" },
    { id: "fraud",         label: "Suspected fraud" },
    { id: "other",         label: "Something else" },
];

/**
 * Reusable 3-dot menu for profiles / events / services / cards.
 *
 * Props:
 *  - target:        { id, name, username?, kind: "user"|"event"|"service" }
 *  - onShare?:      () => void               (optional — adds a "Share" item)
 *  - testId:        unique testid root
 *  - position?:     "left" | "right"         (default "right")
 *  - className?:    extra classes for the trigger button
 */
const MoreMenu = ({ target, onShare, testId, position = "right", className = "" }) => {
    const { user } = useAuth();
    const [open, setOpen] = useState(false);
    const [view, setView] = useState("menu"); // menu | report | reported
    const [reason, setReason] = useState("");
    const [details, setDetails] = useState("");
    const ref = useRef(null);

    useEffect(() => {
        if (!open) return;
        const onDocClick = (ev) => {
            if (ref.current && !ref.current.contains(ev.target)) {
                setOpen(false);
                setTimeout(() => { setView("menu"); setReason(""); setDetails(""); }, 200);
            }
        };
        document.addEventListener("mousedown", onDocClick);
        document.addEventListener("touchstart", onDocClick);
        return () => {
            document.removeEventListener("mousedown", onDocClick);
            document.removeEventListener("touchstart", onDocClick);
        };
    }, [open]);

    const submitReport = () => {
        if (!reason) return;
        const record = {
            id: `rp_${target.kind}_${target.id}_${Date.now()}`,
            kind: target.kind,
            targetId: target.id,
            targetName: target.name || target.username || null,
            reporterId: user?.id || null,
            reason,
            details: details.trim() || null,
            status: "open",
            createdAt: new Date().toISOString(),
        };
        try {
            const list = JSON.parse(localStorage.getItem("neyra.reports") || "[]");
            list.unshift(record);
            localStorage.setItem("neyra.reports", JSON.stringify(list));
        } catch {}
        try { mirror(SUPABASE_TABLES.REPORTS, record); } catch {}
        setView("reported");
    };

    const isCurrentlyBlocked = () => {
        if (!user || target.kind !== "user" || !target.id) return false;
        return getBlocked(user.id).some((b) => b.id === target.id);
    };

    const blockUser = () => {
        if (!user || target.kind !== "user" || !target.id) return;
        if (isCurrentlyBlocked()) {
            // Unblock — no confirm needed
            toggleBlocked(user.id, { id: target.id, name: target.name, username: target.username });
            setOpen(false);
            setView("unblocked");
            setTimeout(() => { setView("menu"); }, 1400);
            return;
        }
        setView("blockConfirm");
    };

    const confirmBlock = () => {
        toggleBlocked(user.id, { id: target.id, name: target.name, username: target.username });
        setView("blocked");
        setTimeout(() => { setOpen(false); setView("menu"); }, 1400);
    };

    return (
        <div className="relative inline-block" ref={ref}>
            <button
                type="button"
                data-testid={`${testId}-trigger`}
                onClick={() => setOpen((o) => !o)}
                aria-label="More options"
                className={[
                    "h-10 w-10 rounded-full bg-background/85 backdrop-blur-sm border border-border flex items-center justify-center hover:bg-muted active:scale-95 transition-[transform,background-color]",
                    className,
                ].join(" ")}
            >
                <MoreHorizontal size={18} />
            </button>

            {open && (
                <div
                    data-testid={`${testId}-menu`}
                    className={[
                        "absolute z-30 mt-2 w-[260px] rounded-2xl border border-border bg-popover text-popover-foreground shadow-card overflow-hidden",
                        position === "left" ? "left-0" : "right-0",
                    ].join(" ")}
                    role="menu"
                >
                    {view === "menu" && (
                        <ul className="py-1">
                            {onShare && (
                                <MenuItem
                                    testId={`${testId}-share`}
                                    icon={Share2}
                                    label="Share"
                                    onClick={() => { setOpen(false); onShare(); }}
                                />
                            )}
                            <MenuItem
                                testId={`${testId}-report`}
                                icon={Flag}
                                label={`Report ${target.kind === "user" ? "user" : target.kind}`}
                                onClick={() => setView("report")}
                            />
                            {target.kind === "user" && target.id !== user?.id && (
                                <MenuItem
                                    testId={`${testId}-block`}
                                    icon={Ban}
                                    label={isCurrentlyBlocked() ? "Unblock user" : "Block user"}
                                    danger={!isCurrentlyBlocked()}
                                    onClick={blockUser}
                                />
                            )}
                        </ul>
                    )}

                    {view === "blockConfirm" && (
                        <div className="p-4" data-testid={`${testId}-block-confirm`}>
                            <div className="font-display text-[14px] font-semibold">Block this user?</div>
                            <p className="mt-1.5 text-[12.5px] text-muted-foreground">
                                They will no longer be able to message you or appear in your recommendations.
                            </p>
                            <div className="mt-3 flex items-center gap-2">
                                <button
                                    type="button"
                                    onClick={() => setView("menu")}
                                    data-testid={`${testId}-block-cancel`}
                                    className="flex-1 h-10 rounded-xl border border-border bg-secondary text-[12.5px] font-medium hover:bg-muted"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    onClick={confirmBlock}
                                    data-testid={`${testId}-block-confirm-btn`}
                                    className="flex-1 h-10 rounded-xl bg-destructive text-white text-[12.5px] font-medium"
                                >
                                    Block
                                </button>
                            </div>
                        </div>
                    )}

                    {view === "blocked" && (
                        <div className="p-4 text-center" data-testid={`${testId}-blocked`}>
                            <div className="font-display text-[13.5px] font-semibold">User blocked</div>
                            <p className="mt-1 text-[12px] text-muted-foreground">
                                You won't see them in discovery or messages.
                            </p>
                        </div>
                    )}

                    {view === "unblocked" && (
                        <div className="p-4 text-center" data-testid={`${testId}-unblocked`}>
                            <div className="font-display text-[13.5px] font-semibold">User unblocked</div>
                            <p className="mt-1 text-[12px] text-muted-foreground">
                                They can now find and message you again.
                            </p>
                        </div>
                    )}

                    {view === "report" && (
                        <div className="p-3" data-testid={`${testId}-report-form`}>
                            <div className="flex items-center justify-between mb-2">
                                <span className="font-display text-[13.5px] font-semibold">Why are you reporting?</span>
                                <button
                                    type="button"
                                    data-testid={`${testId}-report-close`}
                                    onClick={() => { setView("menu"); setReason(""); setDetails(""); }}
                                    className="h-7 w-7 rounded-full hover:bg-muted flex items-center justify-center"
                                >
                                    <X size={14} />
                                </button>
                            </div>
                            <ul className="space-y-1 max-h-[220px] overflow-y-auto pr-1">
                                {REASONS.map((r) => (
                                    <li key={r.id}>
                                        <button
                                            type="button"
                                            data-testid={`${testId}-report-reason-${r.id}`}
                                            onClick={() => setReason(r.id)}
                                            className={[
                                                "w-full flex items-center gap-2 px-3 py-2 rounded-xl text-[13px] text-left transition-colors",
                                                reason === r.id ? "bg-primary/10 text-primary" : "hover:bg-muted",
                                            ].join(" ")}
                                        >
                                            <span className={[
                                                "h-3.5 w-3.5 rounded-full border-2 inline-block shrink-0",
                                                reason === r.id ? "border-primary bg-primary" : "border-muted-foreground/40",
                                            ].join(" ")} />
                                            {r.label}
                                        </button>
                                    </li>
                                ))}
                            </ul>
                            <textarea
                                data-testid={`${testId}-report-details`}
                                value={details}
                                onChange={(e) => setDetails(e.target.value)}
                                placeholder="More details (optional)"
                                rows={3}
                                maxLength={400}
                                className="mt-2 w-full p-2.5 rounded-xl border border-border bg-secondary outline-none text-[12.5px] resize-none focus:bg-background focus:border-primary"
                            />
                            <button
                                type="button"
                                data-testid={`${testId}-report-submit`}
                                onClick={submitReport}
                                disabled={!reason}
                                className="mt-2 w-full h-10 rounded-full text-primary-foreground font-medium text-[12.5px] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
                                style={{
                                    background: "linear-gradient(135deg, hsl(var(--primary)), hsl(var(--primary) / 0.78))",
                                    boxShadow: "0 8px 22px -10px hsl(var(--primary) / 0.55)",
                                }}
                            >
                                Submit report
                            </button>
                        </div>
                    )}

                    {view === "reported" && (
                        <div className="p-4 text-center" data-testid={`${testId}-report-confirmation`}>
                            <span className="mx-auto h-10 w-10 rounded-full bg-primary/15 text-primary flex items-center justify-center">
                                <Flag size={16} />
                            </span>
                            <p className="mt-2 font-display text-[13.5px] font-semibold">Thanks for letting us know</p>
                            <p className="mt-0.5 text-[11.5px] text-muted-foreground">Our admin team will review it shortly.</p>
                            <button
                                type="button"
                                data-testid={`${testId}-report-confirmation-close`}
                                onClick={() => { setOpen(false); setTimeout(() => { setView("menu"); setReason(""); setDetails(""); }, 200); }}
                                className="mt-3 h-9 px-4 rounded-full bg-secondary text-[12px] font-medium"
                            >
                                Done
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

const MenuItem = ({ icon: Icon, label, onClick, testId, danger }) => (
    <li>
        <button
            type="button"
            data-testid={testId}
            onClick={onClick}
            role="menuitem"
            className={[
                "w-full flex items-center gap-2.5 px-3.5 py-2.5 text-[13px] hover:bg-muted text-left",
                danger ? "text-destructive" : "",
            ].join(" ")}
        >
            <Icon size={14} />
            {label}
        </button>
    </li>
);

export default MoreMenu;
